import { project as projectQuery, projects as projectsQuery } from '@data/queries';
import { Container, Divider, Grid, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import imageUrlFor from '@utils/image';
import sanity from '@utils/sanity';
import { NextSeo } from 'next-seo';
import { ReactElement } from 'react';
import { ProjectProps } from 'src/@types/project';
import { useResponsive } from 'src/hooks';
import OfferGallery from 'src/sections/offers/OfferGallery';
import ProjectDuration from 'src/sections/projects/ProjectDuration';
import ProjectHeader from 'src/sections/projects/ProjectHeader';
import ProjectInfo from 'src/sections/projects/ProjectInfo';
import ProjectRelatedServiceItem from 'src/sections/projects/ProjectRelatedServiceItem';
import ProjectRelatedServices from 'src/sections/projects/ProjectRelatedServices';
import ProjectSimilar from 'src/sections/projects/ProjectSimilar';
import { Breadcrumbs, Page } from '../../src/components';
import { HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT } from '../../src/config';
import Layout from '../../src/layouts';
import Routes from '../../src/routes';
import { Newsletter } from '../../src/sections/newsletter';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: HEADER_MOBILE_HEIGHT,
  [theme.breakpoints.up('md')]: {
    paddingTop: HEADER_DESKTOP_HEIGHT,
  },
}));

// ----------------------------------------------------------------------

type Props = {
  project: ProjectProps;
  projects: ProjectProps[];
};

export default function ProjectPage({ project, projects }: Props) {
  const { title, gallery, seo, header, slug, services } = project;

  const isDesktop = useResponsive('up', 'md');

  return (
    <>
      <NextSeo
        openGraph={{
          images: [{ url: imageUrlFor(header).width(600).url() || '' }],
        }}
        canonical={'https://www.b-e-a.no/prosjekter/' + slug.current}
      />
      <Page seo={seo}>
        <RootStyle>
          <Container sx={{ mb: 10 }}>
            <Breadcrumbs
              sx={{ mt: 5 }}
              links={[
                { name: 'Hjem', href: '/' },
                { name: 'Prosjekter', href: Routes.projects },
                { name: title },
              ]}
            />

            {gallery?.images.length > 0 && <OfferGallery gallery={gallery} />}

            <Grid container spacing={8} direction="row-reverse">
              {isDesktop && (
                <Grid item xs={12} md={5} lg={4}>
                  <Stack spacing={4}>
                    <ProjectInfo project={project} />
                    <ProjectRelatedServices services={services} />
                  </Stack>
                </Grid>
              )}

              <Grid item xs={12} md={7} lg={8}>
                <ProjectHeader project={project} />

                <Divider sx={{ borderStyle: 'dashed', my: 5 }} />
                <ProjectDuration project={project} />
              </Grid>
            </Grid>
            {!isDesktop && (
              <Stack spacing={4} mt={3}>
                <ProjectInfo project={project} />
                <ProjectRelatedServices services={services} />
              </Stack>
            )}
          </Container>
          <ProjectSimilar projects={projects.slice(0, 3)} />

          <Newsletter />
        </RootStyle>
      </Page>
    </>
  );
}

// ----------------------------------------------------------------------

ProjectPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};

export const getStaticPaths = async () => {
  const projects = await sanity.fetch(projectsQuery(100));
  const paths = projects.map((project: ProjectProps) => ({
    params: { slug: project.slug.current },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

// This function gets called at build time on server-side.
export const getStaticProps = async ({ params }: any) => {
  const project = await sanity.fetch(projectQuery, { slug: params.slug });
  const projects = await sanity.fetch(projectsQuery(100));
  return { props: { project, projects } };
};
